import _ from 'lodash'
import pathConfig from '@ps/bridge/path'
import { Module } from 'vuex'
import { RootState } from '@ps/types/store'
import router from '@ps/router'
import { readFile, saveFile } from '@ps/api/editor/file'
// @ts-ignore
import * as qc from 'qc'
import store from '@ps/store'
import {
  gamePlayConfig,
  i18nStoreToGameConfig,
} from '@com/utils/i18n-store-conversion'

interface gameConfigState {
  gameConfig: any
  i18nConfig: gamePlayConfig
}

export type gameConfigParam = {
  key: string
  value: any
}

// 动态参数里面特殊参数集合 key
const CONFIG_SET_KEY = 'gameConfig'

/**
 * 判断配置是否需要处理 value 和 items 的转换
 */
const shouldProcessValueItems = (config: any) => {
  return (
    (config.type === 'object' || config.type === 'arrayObject') &&
    config.value &&
    typeof config.value === 'object'
  )
}

/**
 * 将 value 字段转换为 items 字段，用于兼容旧协议
 * 当 type 为 object 且 value 存在但 items 不存在时，将 value 赋值给 items
 */
function convertValueToItems(gameConfig: any): any {
  if (_.isEmpty(gameConfig)) return gameConfig

  const processConfig = (config: any) => {
    if (!config || typeof config !== 'object') return config

    // 处理需要转换的配置
    if (shouldProcessValueItems(config) && !config.items) {
      if (config.type === 'object' && _.isPlainObject(config.value)) {
        config.items = config.value
      } else if (config.type === 'arrayObject' && Array.isArray(config.value)) {
        config.items = config.value
        // 递归处理 arrayObject 子项中的 value 转换为 items
        config.items.forEach((item: any) => {
          if (item && typeof item === 'object') {
            // 如果子项有 value 但没有 items，也需要转换
            if (item.value && _.isPlainObject(item.value) && !item.items) {
              item.items = item.value
            }
            processConfig(item)
          }
        })
      }
    }

    // 递归处理嵌套结构
    if (config.items) {
      if (Array.isArray(config.items)) {
        config.items.forEach(processConfig)
      } else if (_.isPlainObject(config.items)) {
        _.each(config.items, processConfig)
      }
    }

    if (config.value && _.isPlainObject(config.value)) {
      _.each(config.value, processConfig)
    }

    return config
  }

  const newGameConfig = _.cloneDeep(gameConfig)

  // 处理 gameConfig 下的所有配置
  if (newGameConfig[CONFIG_SET_KEY]) {
    _.each(newGameConfig[CONFIG_SET_KEY], processConfig)
  }

  return newGameConfig
}

/**
 * 保存时删除重复的 items 字段，确保保存为新协议结构
 * 当 items 和 value 结构相同时，删除 items 字段
 */
function removeItemsForSave(gameConfig: any): any {
  if (_.isEmpty(gameConfig)) return gameConfig

  const processConfig = (config: any) => {
    if (!config || typeof config !== 'object') return config

    // 如果满足条件，先将 items 同步到 value，然后删除 items
    if (shouldProcessValueItems(config) && config.items) {
      // 将 items 的值同步到 value 中
      config.value = config.items
      // 删除 items 字段
      delete config.items
    }

    // 递归处理所有嵌套结构
    // 处理 value 中的嵌套结构
    if (config.value && _.isPlainObject(config.value)) {
      _.each(config.value, processConfig)
    }

    // 处理 items 中的嵌套结构（如果还存在的话）
    if (config.items) {
      if (Array.isArray(config.items)) {
        config.items.forEach(processConfig)
      } else if (_.isPlainObject(config.items)) {
        _.each(config.items, processConfig)
      }
    }

    // 特殊处理 arrayObject 类型：递归处理数组中的每个子项
    if (config.type === 'arrayObject' && Array.isArray(config.value)) {
      config.value.forEach((item: any) => {
        if (item && typeof item === 'object') {
          // 给 arrayObject 的子项添加 type 字段，确保 shouldProcessValueItems 能正确判断
          if (!item.type && item.value && item.items) {
            item.type = 'object'
          }
          processConfig(item)
        }
      })
    }

    return config
  }

  const newGameConfig = _.cloneDeep(gameConfig)

  // 处理 gameConfig 下的所有配置
  if (newGameConfig[CONFIG_SET_KEY]) {
    _.each(newGameConfig[CONFIG_SET_KEY], processConfig)
  }

  return newGameConfig
}

function syncGameConfigI18n(gameConfig: any, i18nConfig: gamePlayConfig): any {
  if (_.isEmpty(gameConfig) || _.isEmpty(i18nConfig)) return gameConfig
  const newGameConfig = _.cloneDeep(gameConfig[CONFIG_SET_KEY])
  _.each(i18nConfig, (value, key) => {
    const config = _.get(newGameConfig, key)
    if (config) {
      _.set(newGameConfig, key, {
        ...value,
        ...config,
        title: config.title || value.name,
        title_en: config.title_en || value.name_en,
      })
    }
  })
  return Object.assign({}, gameConfig, { [CONFIG_SET_KEY]: newGameConfig })
}

function removeGameConfigI18n(gameConfig: any): any {
  const newGameConfig = _.cloneDeep(gameConfig)
  const rmI18n = (data: any) => {
    const keys = ['name_en', 'desc_en', 'options_en', 'unit_en', 'title_en']
    for (const key of keys) {
      if (typeof data[key] !== 'undefined') {
        delete data[key]
      }
    }
    if (data.items) {
      // 兼容 items 为数组或对象的情况
      if (Array.isArray(data.items)) {
        for (const item of data.items) {
          rmI18n(item)
        }
      } else if (_.isPlainObject(data.items)) {
        _.each(data.items, rmI18n)
      }
    }
  }
  for (const key in newGameConfig[CONFIG_SET_KEY]) {
    rmI18n(newGameConfig[CONFIG_SET_KEY][key])
  }
  return newGameConfig
}

const gameconfigPath = pathConfig.GAME_CONFIG

const gameConfigModule: Module<gameConfigState, RootState> = {
  namespaced: true,

  state: {
    gameConfig: {},
    i18nConfig: {}, // PT 中的 gameConfig 国际化配置
  },

  mutations: {
    updateGameConfig(state, config) {
      if (config[CONFIG_SET_KEY] && state.gameConfig[CONFIG_SET_KEY]) {
        state.gameConfig[CONFIG_SET_KEY] = Object.assign(
          {},
          state.gameConfig[CONFIG_SET_KEY],
          config[CONFIG_SET_KEY],
        )

        state.gameConfig = syncGameConfigI18n(
          state.gameConfig,
          state.i18nConfig,
        )
      } else {
        state.gameConfig = syncGameConfigI18n(
          Object.assign({}, state.gameConfig, config),
          state.i18nConfig,
        )
      }
    },
    updateI18nConfig(state, config) {
      state.i18nConfig = Object.assign({}, state.i18nConfig, config)
      state.gameConfig = syncGameConfigI18n(state.gameConfig, state.i18nConfig)
    },
  },

  actions: {
    /**
     * 初始化 gameConfig
     */
    async initGameConfig({ commit }) {
      const projectKey = Number(router.currentRoute.query.id)
      // 如果返回的值有 code 说明没有这个文件，有这个文件，会返回这个文件的所有内容
      const config: any = await readFile({
        pid: projectKey,
        path: gameconfigPath,
      })
      if (config.code === 1) {
        await saveFile(
          {
            pid: projectKey,
            path: gameconfigPath,
          },
          JSON.stringify({}, null, 2),
        )
      } else {
        // 获取时转换为兼容格式
        const convertedConfig = convertValueToItems(config)
        commit('updateGameConfig', convertedConfig)
      }
    },

    /**
     * 保存 gameConfig 内容
     */
    async save({ state, commit }, data?: gameConfigParam | gameConfigParam[]) {
      const config = {}

      // 对象
      if (_.isPlainObject(data)) {
        const dataConfig = data as gameConfigParam
        _.set(config, dataConfig.key, dataConfig.value)
      }

      if (_.isArray(data)) {
        const dataConfig = data as gameConfigParam[]
        dataConfig.forEach((item) => {
          _.set(config, item.key, item.value)
        })
      }

      const projectKey = Number(router.currentRoute.query.id)

      const gameConfig = Object.assign({}, state.gameConfig, config)

      // 更新editorConfig国际化内容
      store.dispatch('workspace/editorConfig/updateEditorI18nStore')

      if (store.state.workspace.editorConfig.i18nStore) {
        const i18nStore = store.state.workspace.editorConfig.i18nStore

        const gameConfigStore = i18nStore.filter((item: { type: string }) => {
          return (
            item.type !== 'customEvent' &&
            item.type !== 'tween' &&
            item.type !== 'drawLine'
          )
        })
        const gameConfigI18nConfig = i18nStoreToGameConfig(gameConfigStore)
        // 更新最新的 gameConfig playConfig 数据
        commit('updateI18nConfig', gameConfigI18nConfig)
      }

      const removeI18nGameConfig = removeGameConfigI18n(gameConfig)
      // 保存时删除重复的 items 字段，但内存中保留
      const finalGameConfig = removeItemsForSave(removeI18nGameConfig)

      try {
        const res: any = await saveFile(
          {
            pid: projectKey,
            path: gameconfigPath,
          },
          // 删除 stage_ending_resources、stage_game_resources 历史无用数据
          JSON.stringify(finalGameConfig, null, 2),
        )
        if (res.code === 0) {
          // 内存中仍然使用包含 items 的版本，确保编辑时正常工作
          commit('updateGameConfig', removeI18nGameConfig)
          return
        }
        return Promise.reject(new Error(res.msg || '更新配置失败'))
      } catch (e) {
        return Promise.reject(new Error('更新配置失败'))
      }
    },

    /**
     * 获取归属在场景内的动态参数数据
     * @param state
     * @param scene
     */
    getSceneGameConfig({ state }, scene: string): gameConfigState {
      const gameConfig: any = {}
      _.each(state.gameConfig[CONFIG_SET_KEY], (config, key) => {
        if (config.scene === scene) {
          gameConfig[key] = config
        }
      })
      return gameConfig
    },

    /**
     * 获取没有归属场景的动态参数数据，统一显示在全局设置中
     * 注： 由于 PT 中没有归属场景的参数数据，如果归属到 common 目录的话，会导致 PT 的动态参数有两份，一份在 PT 的玩法配置中，一份在 PT 的常驻元素中，因为针对 editor-config 的拼接逻辑里面，不能把这些未归属的参数放在 common 中
     * @param state
     * @param hideRoot 是否隐藏根目录的 gameConfig， editorConfig 中拼接 PT 的动态参数，不能把根目录的参数放在 common 中， 也不能把未归属场景的参数放在 common 中
     */
    getCommonGameConfig(
      { state, rootState },
      hideRoot?: boolean,
    ): gameConfigState {
      const gameConfig: any = {}
      const sceneListNameSet =
        rootState.workspace.game.sceneManager.sceneList.map(
          (item: qc.Node) => item.uniqueName,
        )
      if (!hideRoot) {
        _.each(state.gameConfig, (config, key) => {
          if (key !== CONFIG_SET_KEY) {
            gameConfig[key] = config
          }
        })
        _.each(state.gameConfig[CONFIG_SET_KEY], (config, key) => {
          if (!sceneListNameSet.includes(config.scene)) {
            gameConfig[key] = config
          }
        })
      }

      return gameConfig
    },

    setGameConfig({ state, commit }, data: gameConfigParam) {
      const config = {}

      const dataConfig = data as gameConfigParam

      // 由于最终消费 gameConfig 的组件是会把 key 拍平，会把 CONFIG_SET_KEY 字段的内容提取放在第一级， 所以更新 key/value 的时候，需要判断这个 key 究竟是在 CONFIG_SET_KEY 下还是在 gameConfig 下
      let activeKey = dataConfig.key
      if (state.gameConfig[dataConfig.key]) {
        activeKey = dataConfig.key
      } else if (state.gameConfig[CONFIG_SET_KEY][dataConfig.key]) {
        activeKey = `${CONFIG_SET_KEY}.${dataConfig.key}`
      }
      _.set(config, activeKey, dataConfig.value)

      commit('updateGameConfig', config)
    },
  },
}

export default gameConfigModule
